import {
  isValidUrl,
  hasNumber,
  addInvalidClass,
  removeInvalidClass,
  getFormFieldIndex
} from './validation-helpers';

export const areAnyTimeFrameTestIdsEqualToDefaultTestId = (currentFormObj: {
  utm_test_id: any;
  rules: string | any[];
}) => {
  let result = false;
  const defaultUtmTestId = currentFormObj.utm_test_id;
  for (let i = 0; i < currentFormObj.rules?.length; i++) {
    if (
      currentFormObj.rules[i].timeframe_enabled &&
      currentFormObj.rules[i].timeframe.utm_test_id === defaultUtmTestId
    ) {
      result = true;
      break;
    }
  }
  return result;
};

export const validateRulesMinimum = (formObj: any, invalidationArray: any[]) => {
  if (
    (formObj.rules.length === 1 && formObj.rules[0].rule_name === '') ||
    formObj.rules.length === 0
  ) {
    invalidationArray.push('At least 1 rule is required to create a new route');
  } else {
    const index = getFormFieldIndex(
      invalidationArray,
      'At least 1 rule is required to create a new route'
    );

    if (index !== -1) {
      invalidationArray.splice(index, 1);
    }
  }
};

export const validateRulesMaximum = (formObj: any, invalidationArray: any[]) => {
  if (formObj.rules.length === 15) {
    invalidationArray.push('Traffic Route cannot have more than 15 Rules');
  } else {
    const index = getFormFieldIndex(
      invalidationArray,
      'Traffic Route cannot have more than 15 Rules'
    );

    if (index !== -1) {
      invalidationArray.splice(index, 1);
    }
  }
};

export const validateTimezone = (formObj: any, invalidationArray: any[]) => {
  if (formObj.timeframe_enabled && !formObj.timeframe.time_zone) {
    invalidationArray.push('A timezone must be selected');

    if (!document.getElementById('timezone')?.classList.contains('invalid')) {
      addInvalidClass('timezone', 'id');
    }
  } else {
    const index = getFormFieldIndex(invalidationArray, 'A timezone must be selected');

    if (index !== -1) {
      invalidationArray.splice(index, 1);
    }

    removeInvalidClass('timezone', 'id');
  }
};

export const validateCustomRetention = (formObj: any, invalidationArray: any[]) => {
  if (
    formObj.customRetention?.customRetentionStatus &&
    (formObj.customRetention.startDate === '' || formObj.customRetention.startDate === undefined)
  ) {
    invalidationArray.push('Reset Variant Retention start date must be selected');
    addInvalidClass('ant-picker', 'class');
  }
  if (formObj.customRetention?.customRetentionStatus && formObj.customRetention.time_zone === '') {
    invalidationArray.push('Reset Variant Retention timezone must be selected');
    if (!document.getElementById('reset-timezone')?.classList.contains('invalid')) {
      addInvalidClass('reset-timezone', 'id');
    }
  }
  if (formObj.customRetention?.customRetentionStatus && formObj.customRetention.time_zone !== '') {
    const index = getFormFieldIndex(
      invalidationArray,
      'Reset Variant Retention timezone must be selected'
    );

    if (index !== -1) {
      invalidationArray.splice(index, 1);
    }

    removeInvalidClass('reset-timezone', 'id');
  }
};

export const validateUtmTestId = (
  formObj: any,
  bulkPlover: any,
  formFieldMappingObj: Record<string, string>,
  invalidationArray: any[]
) => {
  let message: string;

  // TODO(DEV-24309): requires the string to end at the version digits -- relax to allow a suffix.
  if (
    /^utm_[a-zA-Z-]+_exp=[a-zA-Z-]+[0-9]+$/.test(formObj.utm_test_id) &&
    hasNumber(formObj.utm_test_id) &&
    !areAnyTimeFrameTestIdsEqualToDefaultTestId(formObj)
  ) {
    const index = getFormFieldIndex(invalidationArray, 'utm_test_id');
    if (index !== -1) {
      invalidationArray.splice(index, 1);
    }
  } else {
    switch (true) {
      case formObj.utm_test_id === '':
        // checking for bulk plover feature
        message = bulkPlover.isBulkPlover
          ? `Sheet Row ${bulkPlover.currentRow} - ${formFieldMappingObj.utm_test_id} cannot be left blank`
          : `${formFieldMappingObj.utm_test_id} cannot be left blank`;
        break;
      case !hasNumber(formObj.utm_test_id):
        message = bulkPlover.isBulkPlover
          ? `Sheet Row ${bulkPlover.currentRow} - ${formFieldMappingObj.utm_test_id} does not have a test iteration number present, please add one`
          : `${formFieldMappingObj.utm_test_id} does not have a test iteration number present, please add one`;
        break;
      case areAnyTimeFrameTestIdsEqualToDefaultTestId(formObj):
        message = bulkPlover.isBulkPlover
          ? `Sheet Row ${bulkPlover.currentRow} - ${formFieldMappingObj.utm_test_id} cannot have the same value as a Timeframe Route Tracking URL Parameter.  1 or more duplicates were detected`
          : `${formFieldMappingObj.utm_test_id} cannot have the same value as a Timeframe Route Tracking URL Parameter.  1 or more duplicates were detected`;
        break;
      default:
        message = bulkPlover.isBulkPlover
          ? `Sheet Row ${bulkPlover.currentRow} - ${formFieldMappingObj.utm_test_id} is invalid`
          : `${formFieldMappingObj.utm_test_id} is invalid`;
    }

    invalidationArray.push(message);
  }
};

export const validateDefaultUrl = (
  formObj: any,
  bulkPlover: any,
  formFieldMappingObj: Record<string, string>,
  invalidationArray: any[]
) => {
  if (formObj.default_url !== '' && isValidUrl(formObj.default_url)) {
    const index = getFormFieldIndex(invalidationArray, 'default_url');
    if (index !== -1) {
      invalidationArray.splice(index, 1);
    }
  } else {
    // checking for bulk plover feature
    const isBlank = formObj.default_url === '';
    let message: string;
    if (bulkPlover.isBulkPlover) {
      message = isBlank
        ? `Sheet Row ${bulkPlover.currentRow} - ${formFieldMappingObj.default_url} cannot be left blank`
        : `Sheet Row ${bulkPlover.currentRow} - ${formFieldMappingObj.default_url} is an invalid URL`;
    } else {
      message = isBlank
        ? `${formFieldMappingObj.default_url} cannot be left blank`
        : `${formFieldMappingObj.default_url} is an invalid URL`;
    }
    invalidationArray.push(message);
  }
};

export const validatePloverUrl = (
  formObj: any,
  bulkPlover: any,
  formFieldMappingObj: Record<string, string>,
  invalidationArray: any[]
) => {
  // TODO: Swap commented out code with if conditional that is currently in use when ready
  if (
    formObj.plover_url !== '' &&
    (/^[a-zA-Z0-9-]+[a-zA-Z0-9]$/.test(formObj.plover_url) ||
      /^[a-zA-Z0-9-]+(.html)$/.test(formObj.plover_url))
  ) {
    const index = getFormFieldIndex(invalidationArray, 'plover_url');
    if (index !== -1) {
      invalidationArray.splice(index, 1);
    }
  } else {
    // checking for bulk plover feature
    const isBlank = formObj.plover_url === '';
    let message: string;
    if (bulkPlover.isBulkPlover) {
      message = isBlank
        ? `Sheet Row ${bulkPlover.currentRow} - ${formFieldMappingObj.plover_url} cannot be left blank`
        : `Sheet Row ${bulkPlover.currentRow} - ${formFieldMappingObj.plover_url} is in an invalid format`;
    } else {
      message = isBlank
        ? `${formFieldMappingObj.plover_url} cannot be left blank`
        : `${formFieldMappingObj.plover_url} is in an invalid format`;
    }
    invalidationArray.push(message);
  }
};

// Checks segments individually to avoid nesting a quantified group inside another, which SAST flags as a ReDoS risk.
const isValidFolderPathSegments = (folderPath: string): boolean =>
  folderPath.startsWith('/') &&
  folderPath.endsWith('/') &&
  folderPath
    .slice(1, -1)
    .split('/')
    .every((segment: string) => /^[a-zA-Z0-9]{3,}$/.test(segment));

export const validateFolderPath = (
  formObj: any,
  bulkPlover: any,
  formFieldMappingObj: Record<string, string>,
  invalidationArray: any[]
) => {
  let message: string;

  if (formObj.folder_path === '/' || isValidFolderPathSegments(formObj.folder_path)) {
    const index = getFormFieldIndex(invalidationArray, 'folder_path');
    if (index !== -1) {
      invalidationArray.splice(index, 1);
    }
  } else if (formObj.folder_path === '') {
    // checking for bulk plover feature
    message = bulkPlover.isBulkPlover
      ? `Sheet Row ${bulkPlover.currentRow} - ${formFieldMappingObj.folder_path} cannot be left blank`
      : `${formFieldMappingObj.folder_path} cannot be left blank`;
    invalidationArray.push(message);
  } else if (
    !/^[^*|":<>{}[\]\\!?=^%#`()';@&$.,\\+\\-]+$/.test(
      formObj.folder_path.replace(/^\/\/|\/\/$/g, '?')
    )
  ) {
    // validation of folder path if it conatain special character   ? , \ , =, ! , [ ]
    message = bulkPlover.isBulkPlover
      ? `Sheet Row ${bulkPlover.currentRow} - ${formFieldMappingObj.folder_path} is in invalid format`
      : `${formFieldMappingObj.folder_path} is in invalid format`;
    invalidationArray.push(message);
  } else if (!formObj.folder_path.startsWith('/') || !formObj.folder_path.endsWith('/')) {
    message = bulkPlover.isBulkPlover
      ? `Sheet Row ${bulkPlover.currentRow} - ${formFieldMappingObj.folder_path} should start and end with / character`
      : `${formFieldMappingObj.folder_path} should start and end with / character`;
    invalidationArray.push(message);
  } else if (formObj.folder_path.length) {
    const splitFolderPathArray = formObj.folder_path
      .split('/')
      .filter((element: any) => element !== '');
    if (splitFolderPathArray) {
      for (let index = 0; index < splitFolderPathArray.length; index++) {
        const element = splitFolderPathArray[index];
        if (element.length) {
          if (index === 0 && element.length < 3) {
            message = bulkPlover.isBulkPlover
              ? `Sheet Row ${bulkPlover.currentRow} - ${formFieldMappingObj.folder_path} cannot be shorter than 3 characters`
              : `${formFieldMappingObj.folder_path} cannot be shorter than 3 characters`;
            invalidationArray.push(message);
          }
          if (index !== 0 && element.length < 2) {
            message = bulkPlover.isBulkPlover
              ? `Sheet Row ${bulkPlover.currentRow} - Sub path cannot be shorter than 2 characters`
              : 'Sub path cannot be shorter than 2 characters';
            invalidationArray.push(message);
          }
        }
      }
    }
  }
};

export const validateDomain = (
  formObj: any,
  bulkPlover: any,
  formFieldMappingObj: Record<string, string>,
  invalidationArray: any[]
) => {
  let message: string;

  if (formObj.domain !== '') {
    // checking for bulk plover feature
    if (bulkPlover.isBulkPlover && bulkPlover.partnerDomainArray?.length > 0) {
      if (bulkPlover.partnerDomainArray?.indexOf(formObj.domain) === -1) {
        message = `Sheet Row ${
          bulkPlover.currentRow
        } - The domain does not match partner-specific listed domains. These are ${bulkPlover.partnerDomainArray.toString()}`;
        invalidationArray.push(message);
      }
    }
    const index = getFormFieldIndex(invalidationArray, 'domain');
    if (index !== -1) {
      invalidationArray.splice(index, 1);
    }
  } else {
    message = bulkPlover.isBulkPlover
      ? `Sheet Row ${bulkPlover.currentRow} - ${formFieldMappingObj.domain} cannot be left blank`
      : `${formFieldMappingObj.domain} cannot be left blank`;
    invalidationArray.push(message);
  }
};

export const validateVariantsField = (
  formObj: any,
  bulkPlover: any,
  formFieldMappingObj: Record<string, string>,
  invalidationArray: any[]
) => {
  let keyArr: any;
  let message = '';

  for (let j = 0; j < formObj.variants.length; j++) {
    keyArr = Object.keys(formObj.variants[j]);
    for (let k = 0; k < keyArr.length; k++) {
      switch (keyArr[k]) {
        case 'variant_url':
          if (formObj.variants[j][keyArr[k]] !== '' && isValidUrl(formObj.variants[j][keyArr[k]])) {
            const index = getFormFieldIndex(invalidationArray, `${keyArr[k]} ${j + 1}`);
            if (index !== -1) {
              invalidationArray.splice(index, 1);
            }
          } else {
            // checking for bulk plover feature
            const isBlank = formObj.variants[j][keyArr[k]] === '';
            if (bulkPlover.isBulkPlover) {
              message = isBlank
                ? `Sheet Row ${bulkPlover.currentRow} - ${formFieldMappingObj[keyArr[k]]} ${j + 1} cannot be left blank`
                : `Sheet Row ${bulkPlover.currentRow} - ${formFieldMappingObj[keyArr[k]]} ${j + 1} is an invalid URL`;
            } else {
              message = isBlank
                ? `${formFieldMappingObj[keyArr[k]]} ${j + 1} cannot be left blank`
                : `${formFieldMappingObj[keyArr[k]]} ${j + 1} is an invalid URL`;
            }
            invalidationArray.push(message);
          }
          break;
        default:
          if (
            formObj.variants[j][keyArr[k]] &&
            /^[a-zA-Z0-9-]+$/.test(formObj.variants[j][keyArr[k]])
          ) {
            const index = getFormFieldIndex(invalidationArray, `${keyArr[k]} ${j + 1}`);
            if (index !== -1) {
              invalidationArray.splice(index, 1);
            }
          } else {
            if (!/^[a-zA-Z0-9-]+$/.test(formObj.variants[j][keyArr[k]])) {
              message = bulkPlover.isBulkPlover
                ? `Sheet Row ${bulkPlover.currentRow} - ${formFieldMappingObj[keyArr[k]]} ${
                    j + 1
                  } is in an invalid format. Only letters, digits, and dash (-) characters are accepted.'`
                : `${formFieldMappingObj[keyArr[k]]} ${
                    j + 1
                  } is in an invalid format. Only letters, digits, and dash (-) characters are accepted.'`;
            }

            if (!formObj.variants[j][keyArr[k]]) {
              message = bulkPlover.isBulkPlover
                ? `Sheet Row ${bulkPlover.currentRow} - ${formFieldMappingObj[keyArr[k]]} ${j + 1} cannot be left blank`
                : `${formFieldMappingObj[keyArr[k]]} ${j + 1} cannot be left blank`;
            }

            invalidationArray.push(message);
          }
      }
    }
  }
};

export const validateDefaultId = (
  formObj: any,
  bulkPlover: any,
  formFieldMappingObj: Record<string, string>,
  invalidationArray: any[]
) => {
  if (formObj.default_id && /^[a-zA-Z0-9-]+$/.test(formObj.default_id)) {
    const index = getFormFieldIndex(invalidationArray, 'default_id');
    if (index !== -1) {
      invalidationArray.splice(index, 1);
    }
  } else {
    // checking for bulk plover feature
    const isBlank = !formObj.default_id;
    let message: string;
    if (bulkPlover.isBulkPlover) {
      message = isBlank
        ? `Sheet Row ${bulkPlover.currentRow} - ${formFieldMappingObj.default_id} cannot be left blank`
        : `Sheet Row ${bulkPlover.currentRow} - ${formFieldMappingObj.default_id} is in an invalid format. Only letters, digits, and dash (-) characters are accepted.'`;
    } else {
      message = isBlank
        ? `${formFieldMappingObj.default_id} cannot be left blank`
        : `${formFieldMappingObj.default_id} is in an invalid format. Only letters, digits, and dash (-) characters are accepted.'`;
    }
    invalidationArray.push(message);
  }
};

export const validateGenericField = (
  formObj: any,
  fieldName: string,
  bulkPlover: any,
  formFieldMappingObj: Record<string, string>,
  invalidationArray: any[]
) => {
  if (formObj[fieldName]) {
    const index = getFormFieldIndex(invalidationArray, fieldName);
    if (index !== -1) {
      invalidationArray.splice(index, 1);
    }

    removeInvalidClass(fieldName, 'id');
  } else {
    invalidationArray.push(
      bulkPlover.isBulkPlover
        ? `Sheet Row ${bulkPlover.currentRow} - ${formFieldMappingObj[fieldName]} cannot be left blank`
        : `${formFieldMappingObj[fieldName]} cannot be left blank`
    );

    if (!document.getElementById(fieldName)?.classList.contains('invalid')) {
      addInvalidClass(fieldName, 'id');
    }
  }
};
